/**
 * EventDetails Component
 * Collects specific details about the user's event
 * Features:
 * - Form validation with error handling
 * - Auto-save to localStorage
 * - Smart time handling (defaults to PM)
 * - Dynamic activity options based on occasion
 * - Responsive form layout
 * - Form state persistence
 */

import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { MapPin, Users, DollarSign, Calendar, Clock, Timer, Wand2, Utensils, Wine, InfoIcon } from 'lucide-react';
import { FormControl, InputLabel, Select, MenuItem, Checkbox, ListItemText } from '@mui/material';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { DatePicker, DesktopTimePicker } from '@mui/x-date-pickers';
import dayjs from 'dayjs';
import useMediaQuery from '@mui/material/useMediaQuery';
import ProgressTracker from '../components/ProgressTracker';
import MainLayout from '../layouts/MainLayout';
import logger from '../utils/logger';
import Tooltip from '@mui/material/Tooltip';
import { PageSEO } from '../components/SEO/PageSEO';
import useAnalytics from '../hooks/useAnalytics';
import { useEventForm } from '../context/EventFormContext';

// Base styles for form controls - ensures consistent appearance across all inputs
const formControlStyles = {
  width: '100%',
  mb: { xs: 1, sm: 1.5 },
  backgroundColor: 'white',
  borderRadius: '0.5rem',
  '& .MuiOutlinedInput-root': {
    backgroundColor: 'white',
    borderRadius: '0.5rem',
    border: '1px solid rgb(229, 231, 235)',
    '&:hover': {
      borderColor: 'rgb(209, 213, 219)',
    },
    '& .MuiSelect-select': {
      lineHeight: '1.2',
      padding: '12px 14px',
      fontSize: '1rem',
      display: 'flex',
      alignItems: 'center',
      height: '1.4375em',
    },
    '& fieldset': {
      borderColor: 'transparent',
      '&:hover': {
        borderColor: 'transparent',
      }
    },
  }
};

export default function EventDetails() {
  const navigate = useNavigate();
  const { trackEvent, trackStepCompleted, trackStepStart } = useAnalytics();
  const { formData, updateSetScene } = useEventForm();
  
  const [hasAttemptedSubmit, setHasAttemptedSubmit] = useState(false);
  
  const [errors, setErrors] = useState({
    activity: '',
    guestCount: '',
    budget: '',
    date: '',
    startTime: '',
    duration: '',
    foodOption: '',
    drinkOption: '',
    location: ''
  });

  const savedSetScene = formData.setScene || {};

  const [eventDetails, setEventDetails] = useState(() => ({
    occasion: savedSetScene.occasion || '',
    activity: savedSetScene.activity || '',
    guestCount: savedSetScene.guestCount || '',
    budget: savedSetScene.budget || '',
    customBudget: savedSetScene.customBudget || '',
    date: savedSetScene.date ? dayjs(savedSetScene.date) : null,
    startTime: savedSetScene.startTime ? dayjs(`2024-01-01T${savedSetScene.startTime}`) : null,
    duration: savedSetScene.duration || '',
    foodAndBev: {
      needs: savedSetScene.foodAndBev?.needs || [],
      foodOption: savedSetScene.foodAndBev?.foodOption || '',
      drinkOption: savedSetScene.foodAndBev?.drinkOption || ''
    },
    location: {
      neighborhoods: savedSetScene.location?.neighborhoods || []
    }
  }));

  useEffect(() => {
    const filledFieldsCount = Object.keys(savedSetScene).length;
    trackStepStart('Event Details', {
      formStep: 1,
      filledFieldsCount,
      occasion: savedSetScene.occasion || 'Not specified'
    });
    logger.info('Event Details form started', { filledFieldsCount });
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Sync local form state to context (debounced via context)
  useEffect(() => {
    updateSetScene({
      ...eventDetails,
      date: eventDetails.date?.format('YYYY-MM-DD') || null,
      startTime: eventDetails.startTime?.format('HH:mm') || null
    });
  }, [eventDetails, updateSetScene]);

  // Handles input changes and automatically defaults time to PM for common event times
  const handleInputChange = (field, value) => {
    if (field === 'startTime' && value) {
      // Convert to PM if hour is between 1-7 (assumes events typically happen in evening)
      if (!value.format('A')) {
        const hour = value.hour();
        value = value.hour(hour + 12);
      }
    }
    
    // For custom budget, handle dollar formatting
    if (field === 'customBudget') {
      // Remove any non-digit characters except for the dollar sign at the beginning
      let formattedValue = value.replace(/[^\d$]/g, '');
      
      // Remove dollar sign(s) for processing
      formattedValue = formattedValue.replace(/\$/g, '');
      
      // Don't allow empty string to be formatted with $
      if (formattedValue) {
        // Make sure we only have whole dollars (no cents)
        formattedValue = formattedValue.replace(/\.\d*$/, '');
        
        // Add dollar sign if it doesn't exist
        if (!formattedValue.startsWith('$')) {
          formattedValue = '$' + formattedValue;
        }
        
        value = formattedValue;
      }
    }
    
    setEventDetails(prev => ({
      ...prev,
      [field]: value
    }));
    
    // Only show validation errors after first submission attempt
    if (hasAttemptedSubmit) {
      if (value) {
        setErrors(prev => {
          const newErrors = { ...prev };
          delete newErrors[field];
          return newErrors;
        });
      } else {
        setErrors(prev => ({
          ...prev,
          [field]: field === 'customBudget'
            ? 'Please enter your budget (minimum $5,000)'
            : `Please select ${field}`
        }));
      }
    }
  };

  // Checks if all required fields are filled
  const allFieldsFilled = () => {
    return eventDetails.activity && 
           eventDetails.guestCount && 
           (eventDetails.budget && (eventDetails.budget !== 'Exact Amount' || eventDetails.customBudget)) &&
           eventDetails.date?.isValid() && 
           eventDetails.startTime?.isValid() && 
           eventDetails.duration &&
           eventDetails.foodAndBev?.foodOption &&
           eventDetails.foodAndBev?.drinkOption && 
           eventDetails.location.neighborhoods.length > 0;
  };

  // Handles form submission
  const handleSubmit = (e) => {
    e.preventDefault();
    setHasAttemptedSubmit(true);

    // Directly collect form errors
    const formErrors = {};
    
    // Check each required field
    if (!eventDetails.activity) formErrors.activity = 'Please select an activity';
    if (!eventDetails.guestCount) formErrors.guestCount = 'Please select guest count';
    
    // Check budget - either predefined or custom
    if (!eventDetails.budget) {
      formErrors.budget = 'Please select budget';
    } else if (eventDetails.budget === 'Exact Amount') {
      // For custom budget, make sure it's not empty and is a valid dollar amount
      if (!eventDetails.customBudget) {
        formErrors.customBudget = 'Please enter your budget (minimum $5,000)';
      } else if (!eventDetails.customBudget.match(/^\$\d+$/)) {
        formErrors.customBudget = 'Please enter a valid dollar amount (minimum $5,000)';
      } else {
        const amount = parseInt(eventDetails.customBudget.replace(/\$/, ''));
        if (amount < 5000) {
          formErrors.customBudget = 'Budget must be at least $5,000';
        }
      }
    }
    
    if (!eventDetails.date?.isValid()) formErrors.date = 'Please select a valid date';
    if (!eventDetails.startTime?.isValid()) formErrors.startTime = 'Please select a valid time';
    if (!eventDetails.duration) formErrors.duration = 'Please select duration';
    if (!eventDetails.foodAndBev?.foodOption && eventDetails.foodAndBev.needs.includes('needsFood')) formErrors.foodOption = 'Please select a food option';
    if (!eventDetails.foodAndBev?.drinkOption && eventDetails.foodAndBev.needs.includes('needsDrinks')) formErrors.drinkOption = 'Please select a drink option';
    if (eventDetails.location.neighborhoods.length === 0) formErrors.location = 'Please select at least one location';

    // Set errors
    setErrors(formErrors);
    
    // Check if there are any errors
    if (Object.keys(formErrors).length > 0) {
      // Track form validation failure
      trackEvent('Form Validation Failed', {
        form: 'EventDetails',
        fields_with_errors: Object.keys(formErrors),
        page: window.location.pathname
      });
      return;
    }

    // Format and sync to context (which auto-persists to localStorage)
    updateSetScene({
      occasion: eventDetails.occasion,
      activity: eventDetails.activity,
      guestCount: eventDetails.guestCount,
      budget: eventDetails.budget === 'Exact Amount' ? eventDetails.customBudget : eventDetails.budget,
      date: eventDetails.date ? eventDetails.date.format('YYYY-MM-DD') : null,
      startTime: eventDetails.startTime ? eventDetails.startTime.format('HH:mm') : null,
      duration: eventDetails.duration,
      foodAndBev: {
        needs: eventDetails.foodAndBev.needs,
        foodOption: eventDetails.foodAndBev.foodOption,
        drinkOption: eventDetails.foodAndBev.drinkOption
      },
      location: {
        neighborhoods: eventDetails.location.neighborhoods
      }
    });

    trackStepCompleted('Event Details', {
      date: eventDetails.date ? eventDetails.date.format('YYYY-MM-DD') : null,
      time: eventDetails.startTime ? eventDetails.startTime.format('HH:mm A') : null,
      guest_count: eventDetails.guestCount,
      budget: eventDetails.budget === 'Exact Amount' ? eventDetails.customBudget : eventDetails.budget,
      duration: eventDetails.duration,
      food_option: eventDetails.foodAndBev.foodOption,
      drink_option: eventDetails.foodAndBev.drinkOption,
      neighborhoods: eventDetails.location.neighborhoods,
      activity: eventDetails.activity
    });

    // Navigate to the next page
    navigate('/dream');
  };

  const getActivitiesByOccasion = () => {
    switch (eventDetails.occasion) {
      case 'drinks':
        return [
          'Engagement Party', 'Birthday Party', 'Happy Hour', 'Cocktail Party', 'Night Out', 'Bottomless Brunch', 'Day Party', 'Custom Event'
        ];
      case 'private-dining':
        return [
          'Birthday Dinner', 'Engagement Dinner', 'Rehearsal Dinner', 'Business Dinner', 'Custom Event'
        ];
      case 'everything-else':
        return [
          'Engagement Party',
          'Bridal Shower',
          'Baby Shower',
          'Networking',
          'Anniversary Party',
          'Graduation Party',
          'Retirement Party',
          'Private Screening',
          'Fashion Show',
          'Offsite',
          'Holiday Party',
          'Launch Party',
          'Influencer Event',
          'Custom Event'
        ];
      default:
        return [
          'Happy Hour', 'Private Dining', 'Party', 'Meeting',
          'Networking', 'Custom Event'
        ];
    }
  };

  // Get dynamic activities based on occasion
  const activities = getActivitiesByOccasion();

  // Predefined guest count ranges
  const guestCounts = [
    '1-10', '11-20', '21-30', '31-40', '41-50',
    '51-60', '61-70', '71-80', '81-90', '91-100',
    '101-150', '151-200', '200+'
  ];

  // Predefined budget ranges (minimum $5,000)
  const budgetRanges = [
    '$5,000-$7,500',
    '$7,500-$10,000',
    '$10,000-$15,000',
    '$15,000-$20,000',
    '$20,000-$30,000',
    '$30,000-$50,000',
    '$50,000-$100,000',
    'Exact Amount'
  ];

  // Standard event duration options
  const durations = [
    '2 hours', '3 hours', '4 hours', '5 hours', '6+ hours'
  ];

  // Check if viewport is mobile for responsive design
  const isMobile = useMediaQuery('(max-width:640px)');

  // Animation variants for form elements
  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1 // Stagger children animations
      }
    }
  };

  // Animation variants for individual form items
  const itemVariants = {
    hidden: { y: 20, opacity: 0 },
    visible: {
      y: 0,
      opacity: 1
    }
  };


  const handleClear = () => {
    const occasion = eventDetails.occasion;

    setEventDetails({
      occasion,
      activity: '',
      guestCount: '',
      budget: '',
      customBudget: '',
      date: null,
      startTime: null,
      duration: '',
      foodAndBev: { needs: [], foodOption: '', drinkOption: '' },
      location: { neighborhoods: [] }
    });
    
    setErrors({});
    setHasAttemptedSubmit(false);
    
    updateSetScene({ occasion });
  };

  const getTitleByOccasion = () => {
    switch (eventDetails.occasion) {
      case 'drinks':
        return "event";
      case 'private-dining':
        return "the dinner you're planning.";
      case 'special occasion':
        return 'special occasion';
      case 'other':
        return 'event';
      default:
        return 'event';
    }
  };

  const getFieldsStatus = () => {
    const totalFields = 7; // total number of required fields (updated from 6 to 7 to include location)
    const filledFields = [
      eventDetails.activity,
      eventDetails.guestCount,
      eventDetails.budget,
      eventDetails.date?.isValid(),
      eventDetails.startTime?.isValid(),
      eventDetails.duration,
      eventDetails.location.neighborhoods.length > 0 // Added location field check
    ].filter(Boolean).length;

    return {
      filled: filledFields,
      remaining: totalFields - filledFields
    };
  };

  // eslint-disable-next-line no-unused-vars
  const foodOptions = [
    { value: 'passed-hors', label: "Passed Hors d'oeuvre: Small bites served by staff" },
    { value: 'grazing-table', label: 'Grazing Table/Light Bites: Self-serve snacks or platters' },
    { value: 'full-dinner', label: 'Full Dinner: A full meal, plated or buffet-style' },
    { value: 'no-food', label: 'No Food: No food needed' }
  ];
  
  // eslint-disable-next-line
  const drinkOptions = [
    { value: 'open-bar', label: 'Open Bar: Unlimited drinks for a set price' },
    { value: 'bar-tab', label: 'Bar Tab: Drinks capped at a set budget' },
    { value: 'cash-bar', label: 'Cash Bar: Guests pay for their own drinks' },
    { value: 'passed-cocktails', label: 'Passed Cocktails: Pre-made drinks served by staff' }
  ];

  const handleFBChange = (field, value) => {
    setEventDetails(prev => ({
      ...prev,
      foodAndBev: {
        ...prev.foodAndBev,
        [field]: value
      }
    }));
    
    // Only update errors if there's been a submission attempt
    if (hasAttemptedSubmit) {
      if (value) {
        setErrors(prev => {
          const newErrors = { ...prev };
          delete newErrors[field];
          return newErrors;
        });
      } else {
        setErrors(prev => ({
          ...prev,
          [field]: `Please select ${field}`
        }));
      }
    }
  };

  // Handle location selections
  const handleLocationChange = (value) => {
    // Handle special group selections
    if (value.includes('group-downtown')) {
      // Remove the group marker and handle individual neighborhood selection
      value = value.filter(v => v !== 'group-downtown');
      
      // Check if we're selecting or deselecting the group
      const allDowntownSelected = downtownNeighborhoods.every(n => 
        eventDetails.location.neighborhoods.includes(n)
      );
      
      if (allDowntownSelected) {
        // Remove all downtown neighborhoods if they were all selected (toggle off)
        value = value.filter(v => !downtownNeighborhoods.includes(v));
      } else {
        // Add all downtown neighborhoods if not all were selected (toggle on)
        const filtered = value.filter(v => !downtownNeighborhoods.includes(v));
        value = [...filtered, ...downtownNeighborhoods];
      }
    }
    else if (value.includes('group-westside')) {
      value = value.filter(v => v !== 'group-westside');
      const allWestSideSelected = westSideNeighborhoods.every(n => 
        eventDetails.location.neighborhoods.includes(n)
      );
      if (allWestSideSelected) {
        value = value.filter(v => !westSideNeighborhoods.includes(v));
      } else {
        const filtered = value.filter(v => !westSideNeighborhoods.includes(v));
        value = [...filtered, ...westSideNeighborhoods];
      }
    }
    else if (value.includes('group-eastside')) {
      value = value.filter(v => v !== 'group-eastside');
      const allEastSideSelected = eastSideNeighborhoods.every(n => 
        eventDetails.location.neighborhoods.includes(n)
      );
      if (allEastSideSelected) {
        value = value.filter(v => !eastSideNeighborhoods.includes(v));
      } else {
        const filtered = value.filter(v => !eastSideNeighborhoods.includes(v));
        value = [...filtered, ...eastSideNeighborhoods];
      }
    }
    else if (value.includes('group-midtown')) {
      value = value.filter(v => v !== 'group-midtown');
      const allMidtownSelected = midtownNeighborhoods.every(n => 
        eventDetails.location.neighborhoods.includes(n)
      );
      if (allMidtownSelected) {
        value = value.filter(v => !midtownNeighborhoods.includes(v));
      } else {
        const filtered = value.filter(v => !midtownNeighborhoods.includes(v));
        value = [...filtered, ...midtownNeighborhoods];
      }
    }
    else if (value.includes('group-upper-east-side')) {
      value = value.filter(v => v !== 'group-upper-east-side');
      const allUpperEastSelected = upperEastSideNeighborhoods.every(n => 
        eventDetails.location.neighborhoods.includes(n)
      );
      if (allUpperEastSelected) {
        value = value.filter(v => !upperEastSideNeighborhoods.includes(v));
      } else {
        const filtered = value.filter(v => !upperEastSideNeighborhoods.includes(v));
        value = [...filtered, ...upperEastSideNeighborhoods];
      }
    }
    else if (value.includes('group-upper-west-side')) {
      value = value.filter(v => v !== 'group-upper-west-side');
      const allUpperWestSelected = upperWestSideNeighborhoods.every(n => 
        eventDetails.location.neighborhoods.includes(n)
      );
      if (allUpperWestSelected) {
        value = value.filter(v => !upperWestSideNeighborhoods.includes(v));
      } else {
        const filtered = value.filter(v => !upperWestSideNeighborhoods.includes(v));
        value = [...filtered, ...upperWestSideNeighborhoods];
      }
    }
    else if (value.includes('all-manhattan')) {
      const isAllManhattanAlreadySelected = eventDetails.location.neighborhoods.includes('all-manhattan');
      
      if (!isAllManhattanAlreadySelected) {
        // Remove individual Manhattan neighborhoods if they were selected
        const filtered = value.filter(v => !manhattanNeighborhoods.flat().includes(v) && v !== 'all-manhattan');
        value = [...filtered, 'all-manhattan'];
      }
    }
    
    setEventDetails(prev => ({
      ...prev,
      location: {
        ...prev.location,
        neighborhoods: value
      }
    }));
    
    // Only show validation errors after first submission attempt
    if (hasAttemptedSubmit) {
      if (value.length > 0) {
        setErrors(prev => {
          const newErrors = { ...prev };
          delete newErrors.location;
          return newErrors;
        });
      } else {
        setErrors(prev => ({
          ...prev,
          location: 'Please select at least one location'
        }));
      }
    }
  };

  // Manhattan neighborhoods reorganized by area
  const downtownNeighborhoods = [
    'Financial District', 'Battery Park City', 'Tribeca', 
    'SoHo', 'Chinatown', 'Lower East Side'
  ];

  const westSideNeighborhoods = [
    'Greenwich Village', 'West Village', 'Meatpacking District',
    'Chelsea', 'Hudson Yards'
  ];

  const eastSideNeighborhoods = [
    'East Village', 'NoHo', 'Flatiron', 'Union Square',
    'Gramercy', 'Kips Bay', 'Murray Hill', 'Turtle Bay'
  ];

  const midtownEastNeighborhoods = [
    'Midtown East', 'Rockefeller Center', 'Turtle Bay'
  ];

  const midtownWestNeighborhoods = [
    'Midtown West', 'Times Square', 'Garment District'
  ];

  const uptownNeighborhoods = [
    'Lincoln Square', 'Columbus Circle', 'Upper West Side',
    'Upper East Side'
  ];

  const upperEastSideNeighborhoods = [
    'Lenox Hill', 'Carnegie Hill', 'Yorkville', 'Metropolitan Hill'
  ];

  const upperWestSideNeighborhoods = [
    'Lincoln Square', 'Manhattan Valley', 'Morningside Heights'
  ];

  // Combined array of all Manhattan neighborhoods
  const manhattanNeighborhoods = [
    westSideNeighborhoods,
    eastSideNeighborhoods,
    downtownNeighborhoods,
    midtownEastNeighborhoods,
    midtownWestNeighborhoods,
    uptownNeighborhoods,
    upperEastSideNeighborhoods,
    upperWestSideNeighborhoods
  ];

  // Helper functions to check if all neighborhoods in a group are selected
  const isGroupSelected = (group) => {
    return group.every(neighborhood => 
      eventDetails.location.neighborhoods.includes(neighborhood) || 
      eventDetails.location.neighborhoods.includes('all-manhattan')
    );
  };

  const isGroupPartiallySelected = (group) => {
    const hasAny = group.some(neighborhood => 
      eventDetails.location.neighborhoods.includes(neighborhood)
    );
    const hasAll = group.every(neighborhood => 
      eventDetails.location.neighborhoods.includes(neighborhood) || 
      eventDetails.location.neighborhoods.includes('all-manhattan')
    );
    return hasAny && !hasAll;
  };

  const midtownNeighborhoods = [
    'Midtown', 'Midtown East', 'Midtown West', 'Times Square', 'Rockefeller Center',
    'Garment District', 'Theater District'
  ];

  // Replace the sentence case helper with a lowercase helper
  const toAllLowerCase = (str) => str ? str.toLowerCase() : '';

  return (
    <MainLayout
      progressTracker={<ProgressTracker currentStep={2} />}
    >
      {/* Add PageSEO component with noindex */}
      <PageSEO
        title="Event Details | LetsHost"
        description="Fill in details about your event"
        path="/details"
        noindex={true}
      />
      
      <motion.div 
        className="max-w-4xl mx-auto mt-4 bg-gray-50 rounded-3xl shadow-xl overflow-hidden p-6 sm:p-8"
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.8 }}
      >
        <div className="mb-1 sm:mb-3">
          <h1 className="text-2xl sm:text-3xl font-bold">
            {(() => {
              const occ = eventDetails.occasion;
              const activity = eventDetails.activity;
              if (activity) return (<>
                Tell us about the <span className="bg-gradient-to-r from-violet-500 via-fuchsia-500 to-pink-500 bg-clip-text text-transparent">{toAllLowerCase(activity)}</span> you're planning.
              </>);
              if (occ === 'private-dining') return (<>
                Tell us about <span className="bg-gradient-to-r from-violet-500 via-fuchsia-500 to-pink-500 bg-clip-text text-transparent">the dinner you're planning.</span>
              </>);
              if (occ === 'drinks') return (<>
                Tell us about your <span className="bg-gradient-to-r from-violet-500 via-fuchsia-500 to-pink-500 bg-clip-text text-transparent">event</span>
              </>);
              return (<>
                Tell us about your{' '}
                <span className="bg-gradient-to-r from-violet-500 via-fuchsia-500 to-pink-500 bg-clip-text text-transparent">{getTitleByOccasion()}</span>
              </>);
            })()}
          </h1>
        </div>

        <motion.div
          className="w-full pt-1 sm:pt-2"
          variants={containerVariants}
          initial="hidden"
          animate="visible"
        >
          <motion.form 
            onSubmit={handleSubmit}
            className="space-y-2 sm:space-y-3"
          >
            <motion.div 
              className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4 mb-4 sm:mb-6"
              variants={containerVariants}
            >
              <motion.div variants={itemVariants}>
                <FormControl 
                  size={isMobile ? "small" : "medium"}
                  sx={{
                    ...formControlStyles,
                    '& .MuiInputLabel-root': {
                      fontSize: { xs: '0.875rem', xl: '1rem', '2xl': '1.125rem' }
                    },
                    '& .MuiOutlinedInput-root': {
                      fontSize: { xs: '0.875rem', xl: '1rem', '2xl': '1.125rem' }
                    }
                  }}
                  error={hasAttemptedSubmit && !!errors.activity}
                >
                  <InputLabel>
                    <span className="flex items-center">
                      <MapPin className="w-4 h-4 mr-2" />
                      What are we doing?
                    </span>
                  </InputLabel>
                  <Select
                    value={eventDetails.activity}
                    label={
                      <span className="flex items-center">
                        <MapPin className="w-4 h-4 mr-2" />
                        What are we doing?
                      </span>
                    }
                    onChange={(e) => handleInputChange('activity', e.target.value)}
                  >
                    {activities.map(activity => (
                      <MenuItem key={activity} value={activity}>{activity}</MenuItem>
                    ))}
                  </Select>
                  {hasAttemptedSubmit && errors.activity && (
                    <p className="mt-1 text-xs text-red-500">{errors.activity}</p>
                  )}
                </FormControl>
              </motion.div>

              <motion.div variants={itemVariants}>
                <FormControl 
                  size={isMobile ? "small" : "medium"}
                  sx={{
                    ...formControlStyles,
                    '& .MuiInputLabel-root': {
                      fontSize: { xs: '0.875rem', xl: '1rem', '2xl': '1.125rem' }
                    },
                    '& .MuiOutlinedInput-root': {
                      fontSize: { xs: '0.875rem', xl: '1rem', '2xl': '1.125rem' }
                    }
                  }}
                  error={hasAttemptedSubmit && !!errors.guestCount}
                >
                  <InputLabel>
                    <span className="flex items-center">
                      <Users className="w-4 h-4 mr-2" />
                      Guest Count
                    </span>
                  </InputLabel>
                  <Select
                    value={eventDetails.guestCount}
                    label={
                      <span className="flex items-center">
                        <Users className="w-4 h-4 mr-2" />
                        Guest Count
                      </span>
                    }
                    onChange={(e) => handleInputChange('guestCount', e.target.value)}
                  >
                    {guestCounts.map(count => (
                      <MenuItem key={count} value={count}>{count}</MenuItem>
                    ))}
                  </Select>
                  {hasAttemptedSubmit && errors.guestCount && (
                    <p className="mt-1 text-xs text-red-500">{errors.guestCount}</p>
                  )}
                </FormControl>
              </motion.div>

              <motion.div variants={itemVariants}>
                <FormControl 
                  size={isMobile ? "small" : "medium"}
                  sx={{
                    ...formControlStyles,
                    '& .MuiInputLabel-root': {
                      fontSize: { xs: '0.875rem', xl: '1rem', '2xl': '1.125rem' }
                    },
                    '& .MuiOutlinedInput-root': {
                      fontSize: { xs: '0.875rem', xl: '1rem', '2xl': '1.125rem' }
                    }
                  }}
                  error={hasAttemptedSubmit && !!errors.budget}
                >
                  <InputLabel>
                    <span className="flex items-center">
                      <DollarSign className="w-4 h-4 mr-2" />
                      Total Event Budget
                      <Tooltip 
                        title="Includes all costs except tax and tip: venue rental, bar minimum, food, drinks, and service fees"
                        arrow
                        placement="top"
                        disableFocusListener
                        disableTouchListener
                        enterTouchDelay={0}
                        leaveTouchDelay={0}
                      >
                        <InfoIcon className="w-4 h-4 ml-1 text-gray-400 cursor-help" />
                      </Tooltip>
                    </span>
                  </InputLabel>
                  <Select
                    value={eventDetails.budget}
                    label={
                      <span className="flex items-center">
                        <DollarSign className="w-4 h-4 mr-2" />
                        Total Event Budget
                        <InfoIcon className="w-4 h-4 ml-1 text-gray-400" />
                      </span>
                    }
                    onChange={(e) => handleInputChange('budget', e.target.value)}
                  >
                    {budgetRanges.map(range => (
                      <MenuItem key={range} value={range}>{range}</MenuItem>
                    ))}
                  </Select>
                  {hasAttemptedSubmit && errors.budget && (
                    <p className="mt-1 text-xs text-red-500">{errors.budget}</p>
                  )}
                </FormControl>
                
                {/* Conditional custom budget input field */}
                {eventDetails.budget === 'Exact Amount' && (
                  <div className="mt-2">
                    <div className="relative">
                      <input
                        type="text"
                        value={eventDetails.customBudget}
                        onChange={(e) => handleInputChange('customBudget', e.target.value)}
                        placeholder="Enter your budget (min $5,000)"
                        className="w-full p-3 border rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent text-sm"
                        inputMode="numeric"
                      />
                    </div>
                    {hasAttemptedSubmit && errors.customBudget && (
                      <p className="mt-1 text-xs text-red-500">{errors.customBudget}</p>
                    )}
                  </div>
                )}
              </motion.div>

              <motion.div variants={itemVariants}>
                <FormControl 
                  size={isMobile ? "small" : "medium"}
                  sx={{
                    ...formControlStyles,
                    '& .MuiInputLabel-root': {
                      fontSize: { xs: '0.875rem', xl: '1rem', '2xl': '1.125rem' }
                    },
                    '& .MuiOutlinedInput-root': {
                      fontSize: { xs: '0.875rem', xl: '1rem', '2xl': '1.125rem' }
                    }
                  }}
                  error={hasAttemptedSubmit && !!errors.location}
                >
                  <InputLabel>
                    <span className="flex items-center">
                      <MapPin className="w-4 h-4 mr-2" />
                      Location
                    </span>
                  </InputLabel>
                  <Select
                    multiple
                    value={eventDetails.location.neighborhoods}
                    onChange={(e) => handleLocationChange(e.target.value)}
                    label={
                      <span className="flex items-center">
                        <MapPin className="w-4 h-4 mr-2" />
                        Location
                      </span>
                    }
                    MenuProps={{
                      anchorOrigin: {
                        vertical: 'bottom',
                        horizontal: 'left',
                      },
                      transformOrigin: {
                        vertical: 'top',
                        horizontal: 'left',
                      },
                      autoFocus: false
                    }}
                    renderValue={(selected) => {
                      // Special handling for "All Manhattan" or "All Brooklyn"
                      if (selected.includes('all-manhattan') && selected.includes('all-brooklyn')) {
                        return 'All Manhattan, All Brooklyn';
                      } else if (selected.includes('all-manhattan')) {
                        return 'All Manhattan';
                      } else if (selected.includes('all-brooklyn')) {
                        return 'All Brooklyn';
                      }
                      
                      // Limit displayed values to prevent overflow
                      return selected.length > 3 
                        ? `${selected.slice(0, 3).join(', ')} + ${selected.length - 3} more`
                        : selected.join(', ');
                    }}
                  >
                    {/* Manhattan section */}
                    <MenuItem value="all-manhattan">
                      <Checkbox checked={eventDetails.location.neighborhoods.includes('all-manhattan')} />
                      <ListItemText primary="All Manhattan" />
                    </MenuItem>
                    
                    {/* West Side - Clickable Group */}
                    <MenuItem 
                      value="group-westside" 
                      disabled={eventDetails.location.neighborhoods.includes('all-manhattan')}
                      sx={{ 
                        backgroundColor: 'rgba(243, 244, 246, 0.7)',
                        '&:hover': { backgroundColor: 'rgba(243, 244, 246, 0.9)' }
                      }}
                    >
                      <Checkbox 
                        checked={isGroupSelected(westSideNeighborhoods)} 
                        indeterminate={isGroupPartiallySelected(westSideNeighborhoods)}
                        disabled={eventDetails.location.neighborhoods.includes('all-manhattan')}
                      />
                      <ListItemText 
                        primary={<span className="font-semibold text-gray-700">West Side</span>} 
                      />
                    </MenuItem>
                    
                    {westSideNeighborhoods.map(neighborhood => (
                      <MenuItem 
                        key={neighborhood} 
                        value={neighborhood} 
                        disabled={eventDetails.location.neighborhoods.includes('all-manhattan')}
                        sx={{ pl: 4 }} // Indent to show hierarchy
                      >
                        <Checkbox checked={eventDetails.location.neighborhoods.includes(neighborhood) || eventDetails.location.neighborhoods.includes('all-manhattan')} />
                        <ListItemText primary={neighborhood} />
                      </MenuItem>
                    ))}
                    
                    {/* East Side - Clickable Group */}
                    <MenuItem 
                      value="group-eastside" 
                      disabled={eventDetails.location.neighborhoods.includes('all-manhattan')}
                      sx={{ 
                        backgroundColor: 'rgba(243, 244, 246, 0.7)',
                        '&:hover': { backgroundColor: 'rgba(243, 244, 246, 0.9)' }
                      }}
                    >
                      <Checkbox 
                        checked={isGroupSelected(eastSideNeighborhoods)} 
                        indeterminate={isGroupPartiallySelected(eastSideNeighborhoods)}
                        disabled={eventDetails.location.neighborhoods.includes('all-manhattan')}
                      />
                      <ListItemText 
                        primary={<span className="font-semibold text-gray-700">East Side</span>} 
                      />
                    </MenuItem>
                    
                    {eastSideNeighborhoods.map(neighborhood => (
                      <MenuItem 
                        key={neighborhood} 
                        value={neighborhood} 
                        disabled={eventDetails.location.neighborhoods.includes('all-manhattan')}
                        sx={{ pl: 4 }} // Indent to show hierarchy
                      >
                        <Checkbox checked={eventDetails.location.neighborhoods.includes(neighborhood) || eventDetails.location.neighborhoods.includes('all-manhattan')} />
                        <ListItemText primary={neighborhood} />
                      </MenuItem>
                    ))}
                    
                    {/* Downtown - Clickable Group */}
                    <MenuItem 
                      value="group-downtown" 
                      disabled={eventDetails.location.neighborhoods.includes('all-manhattan')}
                      sx={{ 
                        backgroundColor: 'rgba(243, 244, 246, 0.7)',
                        '&:hover': { backgroundColor: 'rgba(243, 244, 246, 0.9)' }
                      }}
                    >
                      <Checkbox 
                        checked={isGroupSelected(downtownNeighborhoods)} 
                        indeterminate={isGroupPartiallySelected(downtownNeighborhoods)}
                        disabled={eventDetails.location.neighborhoods.includes('all-manhattan')}
                      />
                      <ListItemText 
                        primary={<span className="font-semibold text-gray-700">Downtown</span>} 
                      />
                    </MenuItem>
                    
                    {downtownNeighborhoods.map(neighborhood => (
                      <MenuItem 
                        key={neighborhood} 
                        value={neighborhood} 
                        disabled={eventDetails.location.neighborhoods.includes('all-manhattan')}
                        sx={{ pl: 4 }} // Indent to show hierarchy
                      >
                        <Checkbox checked={eventDetails.location.neighborhoods.includes(neighborhood) || eventDetails.location.neighborhoods.includes('all-manhattan')} />
                        <ListItemText primary={neighborhood} />
                      </MenuItem>
                    ))}
                    
                    {/* Midtown - Clickable Group */}
                    <MenuItem 
                      value="group-midtown" 
                      disabled={eventDetails.location.neighborhoods.includes('all-manhattan')}
                      sx={{ 
                        backgroundColor: 'rgba(243, 244, 246, 0.7)',
                        '&:hover': { backgroundColor: 'rgba(243, 244, 246, 0.9)' }
                      }}
                    >
                      <Checkbox 
                        checked={isGroupSelected(midtownNeighborhoods)} 
                        indeterminate={isGroupPartiallySelected(midtownNeighborhoods)}
                        disabled={eventDetails.location.neighborhoods.includes('all-manhattan')}
                      />
                      <ListItemText 
                        primary={<span className="font-semibold text-gray-700">Midtown</span>} 
                      />
                    </MenuItem>
                    
                    {midtownNeighborhoods.map(neighborhood => (
                      <MenuItem 
                        key={neighborhood} 
                        value={neighborhood} 
                        disabled={eventDetails.location.neighborhoods.includes('all-manhattan')}
                        sx={{ pl: 4 }} // Indent to show hierarchy
                      >
                        <Checkbox checked={eventDetails.location.neighborhoods.includes(neighborhood) || eventDetails.location.neighborhoods.includes('all-manhattan')} />
                        <ListItemText primary={neighborhood} />
                      </MenuItem>
                    ))}
                    
                    {/* Upper East Side - Clickable Group */}
                    <MenuItem 
                      value="group-upper-east-side" 
                      disabled={eventDetails.location.neighborhoods.includes('all-manhattan')}
                      sx={{ 
                        backgroundColor: 'rgba(243, 244, 246, 0.7)',
                        '&:hover': { backgroundColor: 'rgba(243, 244, 246, 0.9)' }
                      }}
                    >
                      <Checkbox 
                        checked={isGroupSelected(upperEastSideNeighborhoods)} 
                        indeterminate={isGroupPartiallySelected(upperEastSideNeighborhoods)}
                        disabled={eventDetails.location.neighborhoods.includes('all-manhattan')}
                      />
                      <ListItemText 
                        primary={<span className="font-semibold text-gray-700">Upper East Side</span>} 
                      />
                    </MenuItem>
                    
                    {upperEastSideNeighborhoods.map(neighborhood => (
                      <MenuItem 
                        key={neighborhood} 
                        value={neighborhood} 
                        disabled={eventDetails.location.neighborhoods.includes('all-manhattan')}
                        sx={{ pl: 4 }} // Indent to show hierarchy
                      >
                        <Checkbox checked={eventDetails.location.neighborhoods.includes(neighborhood) || eventDetails.location.neighborhoods.includes('all-manhattan')} />
                        <ListItemText primary={neighborhood} />
                      </MenuItem>
                    ))}

                    {/* Upper West Side - Clickable Group */}
                    <MenuItem 
                      value="group-upper-west-side" 
                      disabled={eventDetails.location.neighborhoods.includes('all-manhattan')}
                      sx={{ 
                        backgroundColor: 'rgba(243, 244, 246, 0.7)',
                        '&:hover': { backgroundColor: 'rgba(243, 244, 246, 0.9)' }
                      }}
                    >
                      <Checkbox 
                        checked={isGroupSelected(upperWestSideNeighborhoods)} 
                        indeterminate={isGroupPartiallySelected(upperWestSideNeighborhoods)}
                        disabled={eventDetails.location.neighborhoods.includes('all-manhattan')}
                      />
                      <ListItemText 
                        primary={<span className="font-semibold text-gray-700">Upper West Side</span>} 
                      />
                    </MenuItem>
                    
                    {upperWestSideNeighborhoods.map(neighborhood => (
                      <MenuItem 
                        key={neighborhood} 
                        value={neighborhood} 
                        disabled={eventDetails.location.neighborhoods.includes('all-manhattan')}
                        sx={{ pl: 4 }} // Indent to show hierarchy
                      >
                        <Checkbox checked={eventDetails.location.neighborhoods.includes(neighborhood) || eventDetails.location.neighborhoods.includes('all-manhattan')} />
                        <ListItemText primary={neighborhood} />
                      </MenuItem>
                    ))}
                  </Select>
                  {hasAttemptedSubmit && errors.location && (
                    <p className="mt-1 text-xs text-red-500">{errors.location}</p>
                  )}
                </FormControl>
              </motion.div>

              <LocalizationProvider dateAdapter={AdapterDayjs}>
                <DatePicker
                  label={
                    <span className="flex items-center">
                      <Calendar className="w-4 h-4 mr-2" />
                      Date
                    </span>
                  }
                  value={eventDetails.date}
                  onChange={(newValue) => handleInputChange('date', newValue)}
                  minDate={dayjs()}
                  slotProps={{
                    textField: {
                      size: isMobile ? "small" : "medium",
                      error: hasAttemptedSubmit && !!errors.date,
                      fullWidth: true,
                      sx: {
                        ...formControlStyles,
                        '& .MuiInputLabel-root': {
                          fontSize: { xs: '0.875rem', xl: '1rem', '2xl': '1.125rem' }
                        },
                        '& .MuiOutlinedInput-root': {
                          fontSize: { xs: '0.875rem', xl: '1rem', '2xl': '1.125rem' },
                          '& .MuiInputAdornment-root': {
                            '& svg': {
                              width: '28px',
                              height: '28px',
                              cursor: 'pointer',
                              color: 'rgb(139, 92, 246)',
                              filter: 'drop-shadow(0 2px 2px rgb(0 0 0 / 0.1))',
                              animation: 'pulse 2s infinite',
                              transition: 'all 0.2s ease',
                              '&:hover': {
                                color: 'rgb(109, 40, 217)',
                                transform: 'scale(1.1)',
                                filter: 'drop-shadow(0 4px 3px rgb(0 0 0 / 0.15))'
                              }
                            },
                            '&::after': {
                              content: '"Click to select"',
                              position: 'absolute',
                              right: '40px',
                              fontSize: '0.75rem',
                              color: 'rgb(107, 114, 128)',
                              pointerEvents: 'none'
                            }
                          }
                        }
                      }
                    }
                  }}
                />
              </LocalizationProvider>

              <LocalizationProvider dateAdapter={AdapterDayjs}>
                <DesktopTimePicker
                  label={
                    <span className="flex items-center">
                      <Clock className="w-4 h-4 mr-2" />
                      Time
                    </span>
                  }
                  value={eventDetails.startTime}
                  onChange={(newValue) => handleInputChange('startTime', newValue)}
                  minutesStep={30}
                  views={['hours', 'minutes']}
                  shouldDisableTime={(value, view) => {
                    if (view === 'minutes') {
                      const minutes = value.minute();
                      return minutes !== 0 && minutes !== 30;
                    }
                    return false;
                  }}
                  skipDisabled
                  ampm={true}
                  ampmInClock={true}
                  slotProps={{
                    textField: {
                      size: isMobile ? "small" : "medium",
                      error: hasAttemptedSubmit && !!errors.startTime,
                      fullWidth: true,
                      sx: {
                        ...formControlStyles,
                        '& .MuiInputLabel-root': {
                          fontSize: { xs: '0.875rem', xl: '1rem', '2xl': '1.125rem' }
                        },
                        '& .MuiOutlinedInput-root': {
                          fontSize: { xs: '0.875rem', xl: '1rem', '2xl': '1.125rem' },
                          '& .MuiInputAdornment-root': {
                            '& svg': {
                              width: '28px',
                              height: '28px',
                              cursor: 'pointer',
                              color: 'rgb(139, 92, 246)',
                              filter: 'drop-shadow(0 2px 2px rgb(0 0 0 / 0.1))',
                              animation: 'pulse 2s infinite',
                              transition: 'all 0.2s ease',
                              '&:hover': {
                                color: 'rgb(109, 40, 217)',
                                transform: 'scale(1.1)',
                                filter: 'drop-shadow(0 4px 3px rgb(0 0 0 / 0.15))'
                              }
                            },
                            '&::after': {
                              content: '"Click to select"',
                              position: 'absolute',
                              right: '40px',
                              fontSize: '0.75rem',
                              color: 'rgb(107, 114, 128)',
                              pointerEvents: 'none'
                            }
                          }
                        }
                      }
                    }
                  }}
                />
              </LocalizationProvider>

              <motion.div variants={itemVariants}>
                <FormControl 
                  size={isMobile ? "small" : "medium"}
                  sx={{
                    ...formControlStyles,
                    '& .MuiInputLabel-root': {
                      fontSize: { xs: '0.875rem', xl: '1rem', '2xl': '1.125rem' }
                    },
                    '& .MuiOutlinedInput-root': {
                      fontSize: { xs: '0.875rem', xl: '1rem', '2xl': '1.125rem' }
                    }
                  }}
                  error={hasAttemptedSubmit && !!errors.duration}
                >
                  <InputLabel>
                    <span className="flex items-center">
                      <Timer className="w-4 h-4 mr-2" />
                      Duration
                    </span>
                  </InputLabel>
                  <Select
                    value={eventDetails.duration}
                    label={
                      <span className="flex items-center">
                        <Timer className="w-4 h-4 mr-2" />
                        Duration
                      </span>
                    }
                    onChange={(e) => handleInputChange('duration', e.target.value)}
                  >
                    {durations.map(duration => (
                      <MenuItem key={duration} value={duration}>{duration}</MenuItem>
                    ))}
                  </Select>
                  {hasAttemptedSubmit && errors.duration && (
                    <p className="mt-1 text-xs text-red-500">{errors.duration}</p>
                  )}
                </FormControl>
              </motion.div>

              <motion.div variants={itemVariants}>
                <FormControl 
                  size={isMobile ? "small" : "medium"}
                  sx={{
                    ...formControlStyles,
                    '& .MuiInputLabel-root': {
                      fontSize: { xs: '0.875rem', xl: '1rem', '2xl': '1.125rem' }
                    },
                    '& .MuiOutlinedInput-root': {
                      fontSize: { xs: '0.875rem', xl: '1rem', '2xl': '1.125rem' }
                    }
                  }}
                >
                  <InputLabel>
                    <span className="flex items-center">
                      <Utensils className="w-4 h-4 mr-2" />
                      Food & Beverage Needs
                    </span>
                  </InputLabel>
                  <Select
                    multiple
                    value={eventDetails.foodAndBev.needs}
                    onChange={(e) => handleFBChange('needs', e.target.value)}
                    label={
                      <span className="flex items-center">
                        <Utensils className="w-4 h-4 mr-2" />
                        Food & Beverage Needs
                      </span>
                    }
                    renderValue={(selected) => selected.map(value => 
                      value === 'needsFood' ? 'Food Service' : 'Drink Service'
                    ).join(', ')}
                  >
                    <MenuItem value="needsFood">
                      <Checkbox checked={eventDetails.foodAndBev.needs.includes('needsFood')} />
                      <ListItemText primary="Food Service" />
                    </MenuItem>
                    <MenuItem value="needsDrinks">
                      <Checkbox checked={eventDetails.foodAndBev.needs.includes('needsDrinks')} />
                      <ListItemText primary="Drink Service" />
                    </MenuItem>
                  </Select>
                </FormControl>
              </motion.div>

              {eventDetails.foodAndBev.needs.includes('needsFood') && (
                <motion.div variants={itemVariants}>
                  <FormControl 
                    size={isMobile ? "small" : "medium"}
                    sx={{
                      ...formControlStyles,
                      '& .MuiInputLabel-root': {
                        fontSize: { xs: '0.875rem', xl: '1rem', '2xl': '1.125rem' }
                      },
                      '& .MuiOutlinedInput-root': {
                        fontSize: { xs: '0.875rem', xl: '1rem', '2xl': '1.125rem' }
                      }
                    }}
                  >
                    <InputLabel>
                      <span className="flex items-center">
                        <Utensils className="w-4 h-4 mr-2" />
                        Food Options
                      </span>
                    </InputLabel>
                    <Select
                      value={eventDetails.foodAndBev.foodOption}
                      onChange={(e) => handleFBChange('foodOption', e.target.value)}
                      label={
                        <span className="flex items-center">
                          <Utensils className="w-4 h-4 mr-2" />
                          Food Options
                        </span>
                      }
                    >
                      <MenuItem value="passed-appetizers">Passed Appetizers / Hors D'oeuvres</MenuItem>
                      <MenuItem value="grazing-table">Grazing Table / Self-Serve Light Bites</MenuItem>
                      <MenuItem value="full-dinner">Full Dinner Service</MenuItem>
                    </Select>
                  </FormControl>
                </motion.div>
              )}

              {eventDetails.foodAndBev.needs.includes('needsDrinks') && (
                <motion.div variants={itemVariants}>
                  <FormControl 
                    size={isMobile ? "small" : "medium"}
                    sx={{
                      ...formControlStyles,
                      '& .MuiInputLabel-root': {
                        fontSize: { xs: '0.875rem', xl: '1rem', '2xl': '1.125rem' }
                      },
                      '& .MuiOutlinedInput-root': {
                        fontSize: { xs: '0.875rem', xl: '1rem', '2xl': '1.125rem' }
                      }
                    }}
                  >
                    <InputLabel>
                      <span className="flex items-center">
                        <Wine className="w-4 h-4 mr-2" />
                        Drink Options
                      </span>
                    </InputLabel>
                    <Select
                      value={eventDetails.foodAndBev.drinkOption}
                      onChange={(e) => handleFBChange('drinkOption', e.target.value)}
                      label={
                        <span className="flex items-center">
                          <Wine className="w-4 h-4 mr-2" />
                          Drink Options
                        </span>
                      }
                    >
                      <MenuItem value="bar-minimum">
                        <span className="font-bold">Bar Minimum</span>: Guests Pay & You Cover Any Balance Due 
                      </MenuItem>
                      <MenuItem value="open-bar">
                        <span className="font-bold">Open Bar</span>: Unlimited Drinks For A Set Price
                      </MenuItem>
                      <MenuItem value="passed-cocktails">
                        <span className="font-bold">Passed Cocktails</span>: Pre-Made Drinks Served By Staff For A Set Price
                      </MenuItem>
                    </Select>
                  </FormControl>
                </motion.div>
              )}
            </motion.div>

            <div className="flex flex-col gap-1 sm:gap-2 mt-2 sm:mt-4">
              <motion.button
                type="submit"
                className={`w-full py-1.5 sm:py-2.5 px-4 sm:px-6 rounded-lg transition-all duration-200 shadow-lg hover:shadow-xl transform hover:-translate-y-1 ${
                  allFieldsFilled() 
                    ? 'bg-gradient-to-r from-emerald-400 to-emerald-600' 
                    : 'bg-gradient-to-r from-violet-600 via-fuchsia-500 to-pink-500'
                } text-white text-base sm:text-lg`}
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                animate={allFieldsFilled() ? {
                  scale: [1, 1.03, 1],
                  transition: { duration: 0.3 }
                } : {}}
              >
                <span className="flex flex-col items-center justify-center">
                  <span className="flex items-center justify-center space-x-2 sm:space-x-3">
                    <Wand2 className="w-4 h-4 sm:w-5 sm:h-5" />
                    <span>Set the Scene ✨</span>
                  </span>
                  {!allFieldsFilled() && hasAttemptedSubmit && (
                    <span className="text-xs mt-1 opacity-90">
                      {getFieldsStatus().remaining} field{getFieldsStatus().remaining !== 1 ? 's' : ''} remaining
                    </span>
                  )}
                </span>
              </motion.button>

              <button
                type="button"
                onClick={handleClear}
                className="text-xs sm:text-sm text-gray-500 hover:text-gray-700 transition-colors self-center"
              >
                Clear all fields
              </button>
            </div>
          </motion.form>
        </motion.div>
      </motion.div>
    </MainLayout>
  );
}
